import React from 'react';
import { observer } from 'mobx-react';
import {
    useApplicationSelectionStore,
    useAuthStore,
    useCarWashStore,
} from '../../../contexts/mobx';
import { useSearchParams } from '../../../utils/use-search-params';
import moment from 'moment';
import style from './style.module.scss';
import { useNavigation } from '../../../utils/use-navigation';
import { lastSelectableDay } from '~containers/analytics/kpis/periods';
import { AnalyticsSearchOptions } from '../../../components/analytics-search-options';
import { PageTopBar } from '../../../components/page-top-bar';
import { Section } from '../../../components/form-fields/section';
import { KpiCard } from '../components/kpi-card';
import { AnalyticsActionBar } from '../components/action-bar';
import { SectionHeader } from '~analytics-components/section-header';
import { KpiLoadProgressBar } from '~analytics-components/kpi-load-progress-bar';
import { KpiCardFromSpec } from '../kpis/kpi-card-from-spec';
import { usePageFilter } from '../overview-page/use-page-filter';
import type { PageFilterQuery } from '../overview-page/use-page-filter';
import { getAllCarwashKpis } from './helpers';
import { ENGAGEMENT_CARDS } from './cards';

/**
 * This page is deliberately mixed. "Carwash engagement" is the only group
 * backed by the analytics envelope, so it renders through the standard
 * KpiCardSpec card path (`ENGAGEMENT_CARDS`); the other seven groups read
 * `carWashStore.transactionStats`, a different endpoint out of scope for
 * this migration, and keep rendering through the legacy `KpiCard`. Do not
 * "finish" the conversion by pointing the remaining seven at envelope
 * sources; none exist for them.
 *
 * No page-level Region filter, unlike the other KpiCardSpec dashboards.
 * `carwash-engagement` declares `regionGrain: 'national'` (neither carwash
 * table carries a province), so a region selection would change nothing for
 * the section below. The seven legacy groups have no region concept either
 * (`fetchTransactionStats` takes no province param), so a page-wide filter
 * would be dead above one section and misleading above the rest.
 */
export const CarwashAnalyticsPage = observer(() => {
    const authStore = useAuthStore();
    const carWashStore = useCarWashStore();
    const navigation = useNavigation();
    const applicationSelectionStore = useApplicationSelectionStore();
    const { hasManagementAccess, isCustomerServiceAgent } = authStore;

    if (!hasManagementAccess && !isCustomerServiceAgent) {
        navigation.goToHome();
    }

    const search = useSearchParams<PageFilterQuery>();
    const { filter, setPeriod, setPreset } = usePageFilter({
        search,
        region: 'none',
    });
    const pageRange = filter.range;
    // Always National: see the file comment above for why this page has no
    // Region control.
    const pageRegion = filter.provinces;

    // Both halves read the always-resolved `pageRange`, so the cards and the
    // legacy groups cannot report two windows. Deps are its bounds, not the
    // object: `usePageFilter` doesn't guarantee a stable reference.
    React.useEffect(() => {
        carWashStore.fetchTransactionStats({
            startDate: moment(pageRange.start).toISOString(),
            // The endpoint compares `<= endDate`: the end day is included only
            // through its last instant.
            endDate: moment(pageRange.end).endOf('day').toISOString(),
        });
    }, [
        pageRange.start,
        pageRange.end,
        carWashStore,
        applicationSelectionStore.selectedApplication,
    ]);

    const kpis = getAllCarwashKpis(carWashStore);

    return (
        <>
            <PageTopBar title="Carwash Analytics" />

            <div className={`${style.dashboard} content_with_margins`}>
                <AnalyticsActionBar>
                    <AnalyticsSearchOptions
                        updateParams={setPeriod}
                        startDate={pageRange.start}
                        endDate={pageRange.end}
                        lastSelectableDay={lastSelectableDay()}
                        periodPresets={{
                            active: filter.preset,
                            onSelect: setPreset,
                        }}
                    />
                </AnalyticsActionBar>

                <KpiLoadProgressBar />

                <section className={style.section}>
                    <SectionHeader title="Carwash engagement" />
                    <div className={style.dashboard_grid}>
                        {ENGAGEMENT_CARDS.map((spec, index) => (
                            <KpiCardFromSpec
                                key={`${spec.source}-${spec.form}-${index}`}
                                spec={spec}
                                pageRange={pageRange}
                                pageRegion={pageRegion}
                                waveKey={filter.waveKey}
                            />
                        ))}
                    </div>
                </section>

                <Section title="Car wash" noCard>
                    <div className={style.kpi_tiles_grid}>
                        {kpis.map((cardProps) => (
                            <KpiCard
                                key={`carwash-card-${cardProps.title}`}
                                {...cardProps}
                            />
                        ))}
                    </div>
                </Section>
            </div>
        </>
    );
});
